import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const ENGRAVING_SERVICE_VARIANT_ID = 49457216749881;

class PersonalizeBlock extends Component {
  #previousFocus = null;
  #engraveModalOpen = false;
  #giftWrapModalOpen = false;
  #engraveNoticeOpen = false;
  #giftNoticeOpen = false;
  #draftWrapVariantId = null;
  #giftSelection = { active: false, variantId: null, title: null };
  #engraveSelection = { active: false, text: null };
  /** @type {Map<HTMLElement, { parent: Node, nextSibling: ChildNode | null }>} */
  #modalHomes = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#boundKeydown);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const activeWrapSwatch = this.refs.giftWrapModal?.querySelector('.personalize-engrave__swatch--active');
    if (activeWrapSwatch) {
      this.#draftWrapVariantId = activeWrapSwatch.dataset.variantId;
    }

    this.#syncEngravePreviewFromPdp();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#boundKeydown);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#restoreAllModals();
    document.body.style.overflow = '';
  }

  /**
   * Move a modal to document.body so it escapes sticky product-details stacking context.
   * @param {HTMLElement | undefined | null} modal
   */
  #portalModal(modal) {
    if (!(modal instanceof HTMLElement) || modal.parentElement === document.body) return;

    this.#modalHomes.set(modal, {
      parent: modal.parentNode,
      nextSibling: modal.nextSibling,
    });
    document.body.appendChild(modal);
  }

  /**
   * Restore a portaled modal to its original location inside the component.
   * @param {HTMLElement | undefined | null} modal
   */
  #unportalModal(modal) {
    if (!(modal instanceof HTMLElement)) return;

    const home = this.#modalHomes.get(modal);
    if (!home) return;

    if (home.parent?.isConnected) {
      if (home.nextSibling?.parentNode === home.parent) {
        home.parent.insertBefore(modal, home.nextSibling);
      } else {
        home.parent.appendChild(modal);
      }
    } else if (this.isConnected) {
      this.appendChild(modal);
    }

    this.#modalHomes.delete(modal);
  }

  #restoreAllModals() {
    for (const modal of [...this.#modalHomes.keys()]) {
      this.#unportalModal(modal);
    }
  }

  hasPersonalization() {
    return this.#giftSelection.active || this.#engraveSelection.active;
  }

  validateForAddToCart() {
    if (this.#giftSelection.active) {
      const swatch = this.#getGiftWrapSwatchByVariantId(this.#giftSelection.variantId);
      if (!swatch || swatch.dataset.variantAvailable === 'false') {
        return {
          ok: false,
          message: 'Selected gift wrap is out of stock',
          openModal: 'gift',
        };
      }
    }

    if (this.#engraveSelection.active) {
      const text = this.#engraveSelection.text?.trim() || '';
      if (!text) {
        return {
          ok: false,
          message: 'Please enter your personalization text',
          openModal: 'engrave',
        };
      }

      if (!/^[a-zA-Z ]+$/.test(text)) {
        return {
          ok: false,
          message: 'Only English letters and spaces are allowed',
          openModal: 'engrave',
        };
      }
    }

    return { ok: true };
  }

  buildCartItems({ variantId, quantity, productTitle }) {
    const pairId = Math.floor(Math.random() * 900000) + 100000;
    const mainProperties = {};

    if (this.#engraveSelection.active) {
      mainProperties.Engraved = 'Yes';
    }

    if (this.#giftSelection.active) {
      mainProperties['Gift Wrapped'] = 'Yes';
    }

    if (this.#giftSelection.active || this.#engraveSelection.active) {
      mainProperties.Pair_id = String(pairId);
    }

    const items = [
      {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
        ...(Object.keys(mainProperties).length > 0 ? { properties: mainProperties } : {}),
      },
    ];

    if (this.#engraveSelection.active) {
      items.push({
        id: ENGRAVING_SERVICE_VARIANT_ID,
        quantity: 1,
        properties: {
          'Engraving Text': this.#engraveSelection.text,
          Product: productTitle,
          Pair_id: String(pairId),
        },
        parent_id: Number(variantId),
      });
    }

    if (this.#giftSelection.active && this.#giftSelection.variantId) {
      items.push({
        id: Number(this.#giftSelection.variantId),
        quantity: 1,
        properties: {
          Product: productTitle,
          Pair_id: String(pairId),
        },
        parent_id: Number(variantId),
      });
    }

    return { items, pairId };
  }

  async addToCartWithPersonalization({ variantId, quantity }) {
    const validation = this.validateForAddToCart();
    if (!validation.ok) {
      this.#openModalForValidation(validation.openModal, validation.message);
      return { ok: false, message: validation.message };
    }

    const productTitle = this.dataset.productTitle || '';
    const { items } = this.buildCartItems({ variantId, quantity, productTitle });

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const cartData = await response.json();
      this.#clearAllSelections();

      document.dispatchEvent(
        new CartAddEvent(cartData, String(variantId), {
          source: 'personalize-block',
          itemCount: items.length,
          productId: this.dataset.productId,
        })
      );

      return { ok: true, cartData, itemCount: items.length, variantId: String(variantId) };
    } catch (error) {
      return { ok: false, message: 'Failed to add to cart. Please try again.' };
    }
  }

  handleOpenModal() {
    this.#previousFocus = document.activeElement;
    this.#portalModal(this.refs.modal);
    this.refs.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.refs.closeBtn.focus();
  }

  handleCloseModal() {
    this.refs.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.#previousFocus?.focus();
  }

  handleOverlayClick() {
    this.handleCloseModal();
  }

  handleOpenEngraveModal() {
    if (!this.refs.engraveModal) return;
    this.#previousFocus = document.activeElement;
    this.#hideEngraveNotice();
    this.#restoreEngraveDraft();
    this.#syncEngravePreviewFromPdp();
    this.#portalModal(this.refs.engraveModal);
    this.refs.engraveModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.#engraveModalOpen = true;
    this.refs.engraveCloseBtn.focus();
  }

  handleCloseEngraveModal() {
    if (!this.refs.engraveModal) return;
    this.#hideEngraveNotice();
    this.refs.engraveModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.#engraveModalOpen = false;
    this.#clearEngraveError();
    this.#previousFocus?.focus();
  }

  handleEngraveOverlayClick() {
    if (this.#engraveNoticeOpen) {
      this.handleEngraveNoticeCancel();
      return;
    }
    this.handleCloseEngraveModal();
  }

  handleEngraveInput() {
    if (!this.refs.engraveInput) return;
    const cleaned = this.refs.engraveInput.value.replace(/[^a-zA-Z ]/g, '');
    if (cleaned !== this.refs.engraveInput.value) {
      this.refs.engraveInput.value = cleaned;
    }
    this.#clearEngraveError();
    this.#syncViewText(cleaned);
  }

  handleEngraveConfirm() {
    const text = this.refs.engraveInput?.value.trim() || '';

    if (!text) {
      this.#showEngraveError('Please enter your personalization text');
      this.refs.engraveInput?.focus();
      return;
    }

    if (!/^[a-zA-Z ]+$/.test(text)) {
      this.#showEngraveError('Only English letters and spaces are allowed');
      this.refs.engraveInput?.focus();
      return;
    }

    this.#showEngraveNotice();
  }

  handleEngraveNoticeConfirm() {
    const text = this.refs.engraveInput?.value.trim() || '';
    this.#engraveSelection = { active: true, text };

    if (this.refs.engraveCheckbox) {
      this.#setCheckboxChecked(this.refs.engraveCheckbox, true);
    }

    this.#syncHostClasses();
    this.#hideEngraveNotice();
    this.handleCloseEngraveModal();
  }

  handleEngraveNoticeCancel() {
    this.#hideEngraveNotice();
  }

  handleEngraveCheckboxClick(event) {
    event.stopPropagation();

    if (this.#engraveSelection.active) {
      this.#clearEngraveSelection({ updateCheckbox: false });
      return;
    }

    event.preventDefault();
    this.handleOpenEngraveModal();
  }

  handleGiftWrapCheckboxClick(event) {
    event.stopPropagation();

    if (this.#giftSelection.active) {
      this.#clearGiftSelection({ updateCheckbox: false });
      return;
    }

    event.preventDefault();
    this.handleOpenGiftWrapModal();
  }

  handleOpenGiftWrapModal() {
    if (!this.refs.giftWrapModal) return;
    this.#previousFocus = document.activeElement;
    this.#hideGiftNotice();
    this.#restoreGiftDraft();
    this.#clearGiftWrapError();
    this.#portalModal(this.refs.giftWrapModal);
    this.refs.giftWrapModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.#giftWrapModalOpen = true;
    this.refs.giftWrapCloseBtn.focus();
  }

  handleCloseGiftWrapModal() {
    if (!this.refs.giftWrapModal) return;
    this.#hideGiftNotice();
    this.refs.giftWrapModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.#giftWrapModalOpen = false;
    this.#clearGiftWrapError();
    this.#previousFocus?.focus();
  }

  handleGiftWrapOverlayClick() {
    if (this.#giftNoticeOpen) {
      this.handleGiftWrapNoticeCancel();
      return;
    }
    this.handleCloseGiftWrapModal();
  }

  handleGiftWrapSwatchClick(event) {
    const swatch = event.target.closest('.personalize-engrave__swatch');
    if (!swatch) return;

    const { variantId, variantImage, variantColor, variantAvailable } = swatch.dataset;
    this.#draftWrapVariantId = variantId;

    if (this.refs.giftWrapFeaturedImg) {
      this.refs.giftWrapFeaturedImg.src = variantImage;
    }

    if (this.refs.giftWrapName) {
      this.refs.giftWrapName.textContent = variantColor;
    }

    const swatches = this.refs.giftWrapModal.querySelectorAll('.personalize-engrave__swatch');
    for (const s of swatches) {
      s.classList.toggle('personalize-engrave__swatch--active', s === swatch);
    }

    this.#updateGiftWrapConfirmState(variantAvailable !== 'false');
    this.#clearGiftWrapError();
  }

  handleGiftWrapConfirm() {
    if (!this.#draftWrapVariantId) {
      this.#showGiftWrapError('Please select a gift wrap');
      return;
    }

    const activeSwatch = this.refs.giftWrapModal?.querySelector('.personalize-engrave__swatch--active');
    if (activeSwatch?.dataset.variantAvailable === 'false') {
      this.#showGiftWrapError('Selected gift wrap is out of stock');
      return;
    }

    this.#showGiftNotice();
  }

  handleGiftWrapNoticeConfirm() {
    const activeSwatch = this.refs.giftWrapModal?.querySelector('.personalize-engrave__swatch--active');

    this.#giftSelection = {
      active: true,
      variantId: this.#draftWrapVariantId,
      title: activeSwatch?.dataset.variantColor || '',
    };

    if (this.refs.giftWrapCheckbox) {
      this.#setCheckboxChecked(this.refs.giftWrapCheckbox, true);
    }

    this.#syncHostClasses();
    this.#hideGiftNotice();
    this.handleCloseGiftWrapModal();
  }

  handleGiftWrapNoticeCancel() {
    this.#hideGiftNotice();
  }

  #showEngraveNotice() {
    if (!this.refs.engraveNotice) return;
    this.refs.engraveNotice.hidden = false;
    this.refs.engraveNotice.setAttribute('aria-hidden', 'false');
    this.#engraveNoticeOpen = true;
    this.refs.engraveNoticeConfirmBtn?.focus();
  }

  #hideEngraveNotice() {
    if (!this.refs.engraveNotice) return;
    this.refs.engraveNotice.hidden = true;
    this.refs.engraveNotice.setAttribute('aria-hidden', 'true');
    this.#engraveNoticeOpen = false;
  }

  #showGiftNotice() {
    if (!this.refs.giftWrapNotice) return;
    this.refs.giftWrapNotice.hidden = false;
    this.refs.giftWrapNotice.setAttribute('aria-hidden', 'false');
    this.#giftNoticeOpen = true;
    this.refs.giftWrapNoticeConfirmBtn?.focus();
  }

  #hideGiftNotice() {
    if (!this.refs.giftWrapNotice) return;
    this.refs.giftWrapNotice.hidden = true;
    this.refs.giftWrapNotice.setAttribute('aria-hidden', 'true');
    this.#giftNoticeOpen = false;
  }

  #onVariantUpdate = (event) => {
    if (String(event.detail.data.productId) !== String(this.dataset.productId)) return;
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.#updateEngravePreviewImage(String(variantId));
  };

  #syncEngravePreviewFromPdp() {
    const variantId = document.querySelector('product-form-component input[ref="variantId"]')?.value;
    if (variantId) {
      this.#updateEngravePreviewImage(variantId);
    }
  }

  #updateEngravePreviewImage(variantId) {
    const images = JSON.parse(this.dataset.engravingImages || '{}');
    const src = images[variantId];
    if (src && this.refs.engraveFeaturedImg) {
      this.refs.engraveFeaturedImg.src = src;
    }
  }

  #syncViewText(value) {
    const viewInput = document.querySelector('#view-text');
    if (viewInput) {
      viewInput.value = value;
    }
  }

  #restoreEngraveDraft() {
    if (!this.refs.engraveInput) return;
    const text = this.#engraveSelection.active ? this.#engraveSelection.text : '';
    this.refs.engraveInput.value = text;
    this.#syncViewText(text);
    this.#clearEngraveError();
  }

  #restoreGiftDraft() {
    const swatches = this.refs.giftWrapModal?.querySelectorAll('.personalize-engrave__swatch');
    if (!swatches?.length) return;

    let targetSwatch = swatches[0];
    if (this.#giftSelection.active && this.#giftSelection.variantId) {
      const confirmedSwatch = this.#getGiftWrapSwatchByVariantId(this.#giftSelection.variantId);
      if (confirmedSwatch) {
        targetSwatch = confirmedSwatch;
      }
    }

    this.#draftWrapVariantId = targetSwatch.dataset.variantId;

    if (this.refs.giftWrapFeaturedImg) {
      this.refs.giftWrapFeaturedImg.src = targetSwatch.dataset.variantImage;
    }

    if (this.refs.giftWrapName) {
      this.refs.giftWrapName.textContent = targetSwatch.dataset.variantColor;
    }

    for (const swatch of swatches) {
      swatch.classList.toggle('personalize-engrave__swatch--active', swatch === targetSwatch);
    }

    this.#updateGiftWrapConfirmState(targetSwatch.dataset.variantAvailable !== 'false');
  }

  #getGiftWrapSwatchByVariantId(variantId) {
    if (!variantId || !this.refs.giftWrapModal) return null;
    return this.refs.giftWrapModal.querySelector(`.personalize-engrave__swatch[data-variant-id="${variantId}"]`);
  }

  #updateGiftWrapConfirmState(isAvailable) {
    if (!this.refs.giftWrapConfirmBtn) return;

    if (isAvailable) {
      this.refs.giftWrapConfirmBtn.disabled = false;
      this.refs.giftWrapConfirmBtn.textContent = 'Confirm';
      this.refs.giftWrapConfirmBtn.classList.remove('personalize-engrave__modal-add-btn--sold-out');
    } else {
      this.refs.giftWrapConfirmBtn.disabled = true;
      this.refs.giftWrapConfirmBtn.textContent = 'Sold out';
      this.refs.giftWrapConfirmBtn.classList.add('personalize-engrave__modal-add-btn--sold-out');
    }
  }

  #openModalForValidation(openModal, message) {
    if (openModal === 'gift') {
      this.handleOpenGiftWrapModal();
      this.#showGiftWrapError(message);
      return;
    }

    if (openModal === 'engrave') {
      this.handleOpenEngraveModal();
      this.#showEngraveError(message);
    }
  }

  #showEngraveError(message) {
    if (!this.refs.engraveError) return;
    this.refs.engraveError.textContent = message;
    this.refs.engraveError.hidden = false;
    this.refs.engraveInput?.setAttribute('aria-invalid', 'true');
  }

  #clearEngraveError() {
    if (!this.refs.engraveError) return;
    this.refs.engraveError.hidden = true;
    this.refs.engraveError.textContent = '';
    this.refs.engraveInput?.removeAttribute('aria-invalid');
  }

  #showGiftWrapError(message) {
    if (!this.refs.giftWrapError) return;
    this.refs.giftWrapError.textContent = message;
    this.refs.giftWrapError.hidden = false;
  }

  #clearGiftWrapError() {
    if (!this.refs.giftWrapError) return;
    this.refs.giftWrapError.hidden = true;
    this.refs.giftWrapError.textContent = '';
  }

  #setCheckboxChecked(checkbox, checked) {
    checkbox.checked = checked;
    if (checked) {
      checkbox.setAttribute('checked', 'checked');
    } else {
      checkbox.removeAttribute('checked');
    }
  }

  #syncHostClasses() {
    this.classList.toggle('has_engraving', this.#engraveSelection.active);
    this.classList.toggle('has_gift_wrap', this.#giftSelection.active);
  }

  #clearGiftSelection({ updateCheckbox = true } = {}) {
    this.#giftSelection = { active: false, variantId: null, title: null };
    if (updateCheckbox && this.refs.giftWrapCheckbox) {
      this.#setCheckboxChecked(this.refs.giftWrapCheckbox, false);
    }
    this.#syncHostClasses();
    this.#clearGiftWrapError();
  }

  #clearEngraveSelection({ updateCheckbox = true } = {}) {
    this.#engraveSelection = { active: false, text: null };
    if (updateCheckbox && this.refs.engraveCheckbox) {
      this.#setCheckboxChecked(this.refs.engraveCheckbox, false);
    }
    if (this.refs.engraveInput) {
      this.refs.engraveInput.value = '';
    }
    this.#syncViewText('');
    this.#syncHostClasses();
    this.#clearEngraveError();
  }

  #clearAllSelections() {
    this.#clearGiftSelection();
    this.#clearEngraveSelection();
  }

  #boundKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#engraveNoticeOpen) {
      this.handleEngraveNoticeCancel();
      return;
    }

    if (this.#giftNoticeOpen) {
      this.handleGiftWrapNoticeCancel();
      return;
    }

    if (this.#giftWrapModalOpen) {
      this.handleCloseGiftWrapModal();
      return;
    }

    if (this.#engraveModalOpen) {
      this.handleCloseEngraveModal();
      return;
    }

    if (this.refs.modal?.getAttribute('aria-hidden') === 'true') return;
    this.handleCloseModal();
  };
}

if (!customElements.get('personalize-block')) {
  customElements.define('personalize-block', PersonalizeBlock);
}

const engraveInput = document.querySelector('#engrave-text-input');
const viewInput = document.querySelector('#view-text');

if (engraveInput && viewInput) {
  engraveInput.addEventListener('input', (e) => {
    viewInput.value = e.target.value;
  });
}
